import Modal from '../Modal'
import { useGameStore } from '../../store/gameStore'
import { useI18n } from '../../i18n/I18nProvider'

function HerbariumModal({ isOpen, onClose }) {
  const config = useGameStore((state) => state.config)
  const herbarium = useGameStore((state) => state.herbarium)
  const { t, locale, messages } = useI18n()

  const plants = config?.plants || {}
  const entries = Object.keys(plants).map((plantId) => {
    const entry = herbarium?.[plantId] || null
    const plantConfig = plants[plantId]
    const translation = messages?.plants?.[plantId]

    return {
      id: plantId,
      name: translation?.name || plantConfig?.name || plantId,
      description: translation?.description || plantConfig?.description || '',
      discovered: Boolean(entry),
      snapshot: entry?.snapshot || null,
      discoveredAt: entry?.discoveredAt || null,
    }
  })

  const discoveredCount = entries.filter((entry) => entry.discovered).length
  const totalCount = entries.length

  const formatDate = (timestamp) => {
    if (!timestamp) return ''
    try {
      const formatter = new Intl.DateTimeFormat(locale || 'fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      return formatter.format(new Date(timestamp))
    } catch (error) {
      console.warn('Failed to format date:', error)
      return new Date(timestamp).toLocaleDateString()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('herbarium.modalTitle')} size="lg">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-sm font-medium text-gray-600">
            {t('herbarium.progressLabel')} :{' '}
            <span className="text-green-700 font-bold">
              {discoveredCount}/{totalCount || '—'}
            </span>
          </p>
        </div>

        {entries.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-lg font-semibold text-gray-700 mb-2">
              {t('herbarium.emptyStateTitle')}
            </p>
            <p className="text-gray-500">
              {t('herbarium.emptyStateBody')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className={`relative rounded-2xl border p-3 flex flex-col gap-2 transition shadow-sm ${
                  entry.discovered
                    ? 'bg-white border-green-100'
                    : 'bg-gray-50 border-dashed border-gray-300 text-gray-400'
                }`}
              >
                <div className="aspect-square rounded-xl overflow-hidden bg-gradient-to-b from-green-50 to-green-100 flex items-center justify-center">
                  {entry.discovered && entry.snapshot ? (
                    <img
                      src={entry.snapshot}
                      alt={entry.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-4xl font-bold">?</span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <p className={`text-base font-semibold ${entry.discovered ? 'text-gray-900' : 'text-gray-400'}`}>
                    {entry.discovered ? entry.name : t('herbarium.unknownName')}
                  </p>
                  <p className="text-xs text-gray-500 min-h-[2.5rem]">
                    {entry.discovered ? entry.description : t('herbarium.unknownDescription')}
                  </p>
                  {entry.discovered && (
                    <p className="text-xs text-gray-400">
                      {t('herbarium.discoveredOn')} {formatDate(entry.discoveredAt)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default HerbariumModal
